import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import axios from 'axios';
import { atom, useAtom } from 'jotai';
import { setAuthToken } from '../services/api/tokenStore';
import emitter from '../services/api/eventEmitter';
import { authTransfer } from '../services/auth-transfer.service';
import { isMobileDevice } from '../utils/device';

// Define TypeScript Types
interface AuthorizationToken {
    exp: number;
    idToken: string;
    username: string;
    walletAddress: string;
}

interface AuthContextType {
    authKey?: string;
    isAuthenticated: boolean;
    signNonce: () => Promise<boolean>;
    signInViaLedger: () => Promise<boolean>;
    logout: () => void;
}

// Create Jotai Atom for Authorization Token Storage
const authorizationAtom = atom<AuthorizationToken | undefined>();

// Create AuthContext
const AuthContext = createContext<AuthContextType>({
    isAuthenticated: false,
    signNonce: async () => false,
    signInViaLedger: async () => false,
    logout: () => { },
});

// Get the hash of the nonce
const getHash = (nonce: string) => {
    const prefixString = `I am signing my one-time nonce: ${nonce}`;
    const message = new TextEncoder().encode(prefixString);
    return message;
};

// AuthProvider Component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const wallet = useWallet();
    const [authorization, setAuthorization] = useAtom(authorizationAtom);
    const [authInProgress, setAuthInProgress] = useState(false);

    // Add wallet address verification
    const verifyWalletMatch = useCallback((): boolean => {
        if (!authorization || !wallet.publicKey) return false;
        return authorization.walletAddress === wallet.publicKey.toString();
    }, [authorization, wallet.publicKey]);

    // Modify signNonce to store wallet address
    const signNonce = useCallback(async (): Promise<boolean> => {
        try {
            if (!wallet.publicKey || !wallet.connected || !wallet.signMessage) {
                console.warn('Wallet is not connected.');
                return false;
            }

            if (authInProgress) {
                console.warn('Authentication already in progress.');
                return false;
            }

            setAuthInProgress(true);

            // Fetch nonce from backend
            const response = await axios.get(`/wallets/${wallet.publicKey.toString()}/nonce`, {
                baseURL: process.env.REACT_APP_HIVEMIND_API_URL,
            });

            const nonce = response.data.payload.nonce;
            if (!nonce) {
                console.error('Nonce not received from backend.');
                setAuthInProgress(false);
                return false;
            }
            // Add delay for mobile wallets to ensure proper initialization
            if (isMobileDevice()) {
                await new Promise(resolve => setTimeout(resolve, 500));
            }

            // Sign the nonce
            const signature = await wallet.signMessage(getHash(nonce));
            if (!signature) {
                console.error('Failed to sign nonce.');
                setAuthInProgress(false);
                return false;
            }

            // Authorize wallet with signed nonce
            const authResponse = await axios.post(
                `/authorize`,
                {
                    walletId: wallet.publicKey.toString(),
                    signature: Buffer.from(signature).toString("base64"),
                },
                {
                    baseURL: process.env.REACT_APP_HIVEMIND_API_URL,
                }
            );

            const authData: AuthorizationToken = {
                ...authResponse.data.payload,
                walletAddress: wallet.publicKey.toString(),
            };
            if (!authData || !authData.idToken) {
                console.error('Authorization token not received.');
                setAuthInProgress(false);
                return false;
            }

            // Set authorization data
            setAuthorization(authData);
            setAuthInProgress(false);
            return true;
        } catch (error: any) {
            const errorMessage = error?.message || String(error);

            // Handle "not authorized" - disconnect for reconnect with proper permissions
            if (errorMessage.includes('not been authorized') ||
                errorMessage.includes('Unauthorized')) {
                console.warn('Wallet signing not authorized, disconnecting');
                await wallet?.disconnect?.();
                setAuthInProgress(false);
                return false;
            }

            // Handle user rejection gracefully
            if (errorMessage.includes('User rejected') ||
                errorMessage.includes('cancelled')) {
                console.log('User cancelled signing request');
                setAuthInProgress(false);
                return false;
            }

            console.error('Error during signNonce:', error);
            setAuthInProgress(false);
            return false;
        }
    }, [wallet, authInProgress, setAuthorization]);

    // Add signInViaLedger function
    const signInViaLedger = useCallback(async (): Promise<boolean> => {
        try {
            if (!wallet.publicKey || !wallet.connected) {
                console.warn('Wallet is not connected.');
                return false;
            }

            if (authInProgress) {
                console.warn('Authentication already in progress.');
                return false;
            }

            setAuthInProgress(true);

            // Perform ledger authentication via transaction
            const tx = await authTransfer(wallet);
            if (!tx) {
                console.error('Failed to send authentication transaction.');
                setAuthInProgress(false);
                return false;
            }

            // Authorize wallet with transaction signature
            const authResponse = await axios.post(
                `/authorize/${wallet.publicKey.toString()}`,
                {
                    signature: tx,
                },
                {
                    baseURL: process.env.REACT_APP_HIVEMIND_API_URL,
                }
            );

            const authData: AuthorizationToken = {
                ...authResponse.data.payload,
                walletAddress: wallet.publicKey.toString(),
            };
            if (!authData || !authData.idToken) {
                console.error('Authorization token not received.');
                setAuthInProgress(false);
                return false;
            }

            // Set authorization data
            setAuthorization(authData);
            setAuthInProgress(false);
            return true;
        } catch (error) {
            console.error('Error during signInViaLedger:', error);
            setAuthInProgress(false);
            return false;
        }
    }, [wallet, authInProgress, setAuthorization]);

    // Initialize Authentication on Wallet Connection or Disconnection
    useEffect(() => {
        const initializeAuth = async () => {
            if (!wallet.publicKey || !wallet.connected) {
                setAuthorization(undefined);
                setAuthToken('');
                return;
            }
        };

        initializeAuth();
    }, [wallet.publicKey, wallet.connected, setAuthorization]);

    // Implement the logout function
    const logout = useCallback((): void => {
        setAuthorization(undefined);
        setAuthToken('');
    }, [setAuthorization]);

    // Listen for logout events emitted by Axios
    useEffect(() => {
        const handleLogout = () => {
            logout();
        };

        emitter.on('logout', handleLogout);

        return () => {
            emitter.off('logout', handleLogout);
        };
    }, [logout]);

    // Update the auth token whenever authorization changes
    useEffect(() => {
        if (authorization?.idToken) {
            setAuthToken(authorization.idToken);
        } else {
            setAuthToken('');
        }
    }, [authorization]);

    // Add effect to monitor wallet changes
    useEffect(() => {
        if (authorization && wallet.publicKey) {
            const isWalletMatch = verifyWalletMatch();
            if (!isWalletMatch) {
                console.warn('Wallet mismatch detected. Logging out.');
                logout();
            }
        }
    }, [wallet.publicKey, authorization, verifyWalletMatch, logout]);

    return (
        <AuthContext.Provider
            value={{
                authKey: authorization?.idToken,
                isAuthenticated: !!authorization && verifyWalletMatch(),
                signNonce,
                signInViaLedger,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

// Custom Hook to Use AuthContext
export const useAuth = (): AuthContextType => {
    return useContext(AuthContext);
}; 